class OrganaxProductSticky extends HTMLElement {
  connectedCallback() {
    this.info = this.closest('product-info');
    if (!this.info) return;
    this.listeners = new AbortController();
    this.quantity = this.querySelector('[data-sticky-quantity]');
    this.submit = this.querySelector('[data-sticky-submit]');
    this.price = this.querySelector('[data-sticky-price]');
    this.choice = this.querySelector('[data-sticky-choice]');
    this.schedule = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
    };
    this.onChange = () => { this.appPrice = null; this.schedule(); };
    this.info.addEventListener('change', this.onChange);
    this.observer = new MutationObserver(mutations => {
      if (mutations.some(mutation => !this.contains(mutation.target))) this.schedule();
    });
    this.observer.observe(this.info, { childList: true, subtree: true, attributes: true, characterData: true, attributeFilter: ['disabled', 'aria-disabled', 'class', 'value'] });
    this.intersection = new IntersectionObserver(() => this.syncVisibility());
    this.onScroll = () => {
      if (this.visibilityFrame) return;
      this.visibilityFrame = requestAnimationFrame(() => { this.visibilityFrame = null; this.syncVisibility(); });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.quantity.addEventListener('change', () => {
      const input = this.info.querySelector('.product-form__quantity input[name="quantity"]');
      if (!input || !this.quantity.checkValidity()) return;
      input.value = this.quantity.value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }, { signal: this.listeners.signal });
    this.querySelectorAll('[data-sticky-step]').forEach(button => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        if (button.dataset.stickyStep === '1') this.quantity.stepUp();
        else this.quantity.stepDown();
        this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }, { signal: this.listeners.signal });
    });
    this.submit.addEventListener('click', () => {
      if (!this.quantity.checkValidity()) { this.quantity.reportValidity(); return; }
      const source = this.info.querySelector('.product-form__submit');
      if (!source || source.disabled || source.getAttribute('aria-disabled') === 'true') return;
      // One real form submits its current variant, quantity and selling plan.
      source.click();
      this.schedule();
    }, { signal: this.listeners.signal });
    this.onAppPrice = event => {
      const detail = event.detail;
      const form = this.info.querySelector('form[data-type="add-to-cart-form"]');
      const variant = form?.querySelector('[name="id"]')?.value;
      const plan = this.info.querySelector('[data-organax-selling-plan]')?.value;
      if (!detail || detail.surface !== 'product-page' || String(detail.variantId) !== variant || plan || typeof detail.discountedPrice !== 'number') return;
      // Listen to the app's emitted price; never compute its campaign discount.
      try {
        this.appPrice = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: detail.currencyCode || window.Shopify?.currency?.active || 'GBP' }).format(detail.discountedPrice);
        this.schedule();
      } catch { /* Keep the native displayed price if the currency is unavailable. */ }
    };
    document.addEventListener('dmixer:price-changed', this.onAppPrice);
    this.onViewport = () => {
      const viewport = window.visualViewport;
      const offset = viewport && this.contains(document.activeElement) ? Math.max(0, innerHeight - viewport.height - viewport.offsetTop) : 0;
      this.style.setProperty('--sticky-keyboard-offset', `${offset}px`);
    };
    window.visualViewport?.addEventListener('resize', this.onViewport);
    window.visualViewport?.addEventListener('scroll', this.onViewport);
    this.sync();
  }

  sync() {
    const source = this.info.querySelector('.product-form__submit');
    if (source !== this.source) {
      if (this.source) this.intersection.unobserve(this.source);
      this.source = source;
      if (source) this.intersection.observe(source);
    }
    if (!source) { this.hidden = true; return; }
    const disabled = source.disabled || source.getAttribute('aria-disabled') === 'true';
    this.submit.disabled = disabled;
    const loading = source.classList.contains('loading');
    this.submit.setAttribute('aria-busy', String(loading));
    this.setText(this.submit, loading ? this.dataset.loadingLabel : source.querySelector('span')?.textContent.trim());
    const plan = this.info.querySelector('[data-organax-selling-plan]')?.value;
    const selected = this.info.querySelector('[data-organax-purchase] input[type="radio"]:checked');
    const frequency = selected?.closest('.organax-product__plan-card')?.querySelector('[data-organax-plan-select]');
    this.setText(this.choice, plan ? frequency?.selectedOptions[0]?.textContent || 'Subscription' : 'One-time purchase');
    const headline = this.info.querySelector('[data-organax-current-amount]');
    const priceRoot = this.info.querySelector('[id^="price-"]');
    const appHost = priceRoot?.querySelector('dmixer-discounted-price');
    const appAmount = appHost?.shadowRoot?.querySelector('[part~="discounted-price"]')?.textContent;
    const nativePrice = priceRoot?.querySelector('.price--on-sale .price-item--sale') || priceRoot?.querySelector('.price__regular .price-item--regular');
    this.setText(this.price, plan ? headline?.textContent.trim() : this.appPrice || appAmount || headline?.textContent.trim() || nativePrice?.textContent.trim());
    const input = this.info.querySelector('.product-form__quantity input[name="quantity"]');
    if (input) {
      this.quantity.parentElement.hidden = false;
      for (const name of ['min', 'max', 'step']) {
        const value = name === 'step' ? input.getAttribute('step') || input.dataset.step : input.getAttribute(name);
        if (value !== null) this.quantity.setAttribute(name, value);
        else this.quantity.removeAttribute(name);
      }
      if (document.activeElement !== this.quantity) this.quantity.value = input.value;
      const value = Number(this.quantity.value);
      this.querySelector('[data-sticky-step="-1"]').disabled = value <= Number(this.quantity.min || 1);
      this.querySelector('[data-sticky-step="1"]').disabled = this.quantity.max !== '' && value >= Number(this.quantity.max);
    } else this.quantity.parentElement.hidden = true;
    this.syncVisibility();
  }

  setText(element, value) {
    const text = value || '';
    if (element.textContent !== text) element.textContent = text;
  }

  syncVisibility() {
    const hidden = !this.source || this.source.getBoundingClientRect().bottom >= 0;
    if (this.hidden !== hidden) this.hidden = hidden;
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.observer?.disconnect();
    this.intersection?.disconnect();
    this.info?.removeEventListener('change', this.onChange);
    document.removeEventListener('dmixer:price-changed', this.onAppPrice);
    window.removeEventListener('scroll', this.onScroll);
    window.visualViewport?.removeEventListener('resize', this.onViewport);
    window.visualViewport?.removeEventListener('scroll', this.onViewport);
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.visibilityFrame) cancelAnimationFrame(this.visibilityFrame);
  }
}
if (!customElements.get('organax-product-sticky')) customElements.define('organax-product-sticky', OrganaxProductSticky);
